import { computed, ref, watchEffect } from 'vue';
import { useCoursesStore } from '@/stores/courses';
import { useUserStore } from '@/stores/user';
import { parseLocalDate, toDateStr, todayDateStr } from '@/utils/dateUtils';

export const SESSION_END_OFFSET_DAYS = 14;
export const REFUND_DEADLINE_DAYS = 10;
export const ENROLLMENT_DEADLINE_DAYS = 7;
export const CREDIT_ELIGIBILITY_OFFSET_DAYS = 8;

const CACHE_KEY = 'session_milestones_terms';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 1 day

/**
 * Read cached terms from localStorage
 * @returns {Array|null} Cached terms or null if expired/missing
 */
function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp > CACHE_TTL_MS) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

/**
 * Write terms to localStorage cache
 * @param {Array} terms
 */
function writeCache(terms) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data: terms, timestamp: Date.now() }));
  } catch {
    // localStorage full or unavailable — ignore
  }
}

/**
 * Normalize any date string/timestamp to YYYY-MM-DD using local timezone
 * @param {string} dateString - ISO timestamp or date string
 * @returns {string} Date as YYYY-MM-DD
 */
function toDateOnly(dateString) {
  return toDateStr(parseLocalDate(dateString));
}

/**
 * Subtract days from a date string and return YYYY-MM-DD
 * @param {string} dateString - ISO date string or timestamp
 * @param {number} days - Number of days to subtract
 * @returns {string} Adjusted date as YYYY-MM-DD
 */
export function subtractDays(dateString, days) {
  const date = parseLocalDate(dateString);
  date.setDate(date.getDate() - days);
  return toDateStr(date);
}

/**
 * Add days to a date string (inclusive — day 1 is the start date) and return YYYY-MM-DD
 * @param {string} dateString - ISO date string or timestamp
 * @param {number} days - Number of days to add (inclusive)
 * @returns {string} Adjusted date as YYYY-MM-DD
 */
export function addDaysInclusive(dateString, days) {
  const date = parseLocalDate(dateString);
  date.setDate(date.getDate() + days - 1);
  return toDateStr(date);
}

function isOnDemandTerm(sessionName) {
  const lower = (sessionName || '').toLowerCase();
  return lower === 'on-demand' || lower === 'self paced';
}

/**
 * Build calendar events from session term data
 * @param {Array} terms - Array of term objects from API
 * @param {boolean} isAccelerate - Whether the learner is on the Accelerate platform
 * @param {boolean} isBenefitType5 - Whether the learner is Benefit Type 5 (employer-paid, no charges)
 * @returns {Array} Calendar event objects sorted by date
 */
export function buildSessionMilestoneEvents(terms, isAccelerate, isBenefitType5) {
  if (!terms || !Array.isArray(terms)) return [];

  const today = todayDateStr();
  const events = [];

  terms.forEach((term) => {
    const { id, session_name, hed__start_date, hed__end_date } = term;

    if (!session_name || !hed__start_date || !hed__end_date) return;

    // Skip On-demand/Self Paced terms — handled per-course in useCourseDates
    if (isOnDemandTerm(session_name)) return;

    const startDate = toDateOnly(hed__start_date);

    // Session start event
    if (startDate >= today) {
      events.push({
        id: `session-start-${id}`,
        date: startDate,
        text: `Session starts: ${session_name}`,
        type: 'session_milestone'
      });
    }

    // Session end event (14 days before database end date)
    const adjustedEndDate = subtractDays(hed__end_date, SESSION_END_OFFSET_DAYS);
    if (adjustedEndDate >= today) {
      events.push({
        id: `session-end-${id}`,
        date: adjustedEndDate,
        text: `Session ends: ${session_name}`,
        type: 'session_milestone'
      });
    }

    // Credit eligibility / "ready to add to transcript" (8 days after the displayed session end)
    const creditEligibilityDate = subtractDays(hed__end_date, SESSION_END_OFFSET_DAYS - CREDIT_ELIGIBILITY_OFFSET_DAYS);
    if (creditEligibilityDate >= today) {
      events.push({
        id: `credit-eligible-${id}`,
        date: creditEligibilityDate,
        text: `${session_name} ready to add to transcript`,
        type: 'session_milestone'
      });
    }

    // Enrollment deadline (7 days from session start, inclusive)
    const enrollDeadline = addDaysInclusive(hed__start_date, ENROLLMENT_DEADLINE_DAYS);
    if (enrollDeadline >= today) {
      events.push({
        id: `enroll-session-${id}`,
        date: enrollDeadline,
        text: `Enrollment deadline for ${session_name}`,
        type: 'session_milestone'
      });
    }

    // Refund deadline (10 days from session start, inclusive) — hide for BT5 (employer-paid) and Accelerate
    if (!isBenefitType5 && !isAccelerate) {
      const refundDeadline = addDaysInclusive(hed__start_date, REFUND_DEADLINE_DAYS);
      if (refundDeadline >= today) {
        events.push({
          id: `refund-session-${id}`,
          date: refundDeadline,
          text: isAccelerate
            ? `Last day to avoid charges for ${session_name}`
            : `Enrollment fee refund deadline ${session_name}`,
          type: 'session_milestone'
        });
      }
    }
  });

  events.sort((a, b) => new Date(a.date) - new Date(b.date));
  return events;
}

export function useSessionMilestones() {
  const coursesStore = useCoursesStore();
  const userStore = useUserStore();
  const cachedTerms = ref(readCache() || []);
  const fetchAttempted = ref(false);

  // Try cache first, then fetch from API if needed
  watchEffect(async () => {
    if (fetchAttempted.value) return;

    // If cache has data, use it
    if (cachedTerms.value.length > 0) return;

    // If store already has data, use and cache it
    const storeTerms = coursesStore.learnerCourses?.sessionTerms;
    if (storeTerms?.length) {
      cachedTerms.value = storeTerms;
      writeCache(storeTerms);
      return;
    }

    const learnerId = userStore.apis?.learnerData?._learnerId;
    if (!learnerId) return;

    fetchAttempted.value = true;
    try {
      await coursesStore.fetchCoursesForLearner(learnerId, false);
      const terms = coursesStore.learnerCourses?.sessionTerms || [];
      cachedTerms.value = terms;
      writeCache(terms);
    } catch {
      // Fail silently — session milestones are supplemental
    }
  });

  const sessionTerms = computed(() => {
    // Prefer store data if available (fresher), fall back to cache
    return coursesStore.learnerCourses?.sessionTerms || cachedTerms.value;
  });

  const isAccelerate = computed(() => userStore.apis?.learnerData?._partnerURLSubDomain === 'accelerate');
  const isBenefitType5 = computed(() => {
    const bt = userStore.apis?.learnerData?._benefitType;
    return bt === 5 || bt === '5';
  });

  const sessionMilestoneEvents = computed(() =>
    buildSessionMilestoneEvents(sessionTerms.value, isAccelerate.value, isBenefitType5.value)
  );

  return {
    sessionTerms,
    sessionMilestoneEvents
  };
}
